import { ArrowRight } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { motion } from 'framer-motion';
import { useMemo } from 'react';

// CORRECTED HashRouter base URL to point to munger-app service
const AUTH_APP_URL = "https://auth.munger.ai";

const HeroSectionVariantB = ({ onJoinWaitlist }) => {
  const location = useLocation();

  const handleSignUpClick = (e) => {
    e.preventDefault();
    window.location.href = `${AUTH_APP_URL}/#/choose-plan`;
  };

  const handleSignInClick = (e) => {
    e.preventDefault();
    window.location.href = `${AUTH_APP_URL}/#/login`;
  };

  // Pre-calculated sparkle data for better performance
  const sparkleData = useMemo(() => 
    Array.from({ length: 6 }, (_, i) => ({
      id: i,
      initialX: Math.random() * 100,
      initialY: 100,
      size: Math.random() * 3 + 2,
      opacity: Math.random() * 0.4 + 0.6,
      duration: Math.random() * 8 + 12,
      delay: Math.random() * 8,
    })), []
  );

  // Optimized sparkles component with GPU acceleration
  const Sparkles = () => (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {sparkleData.map((sparkle) => (
        <motion.div
          key={sparkle.id}
          className="absolute rounded-full"
          initial={{ 
            x: `${sparkle.initialX}%`,
            y: '100vh',
            opacity: 0,
            scale: 0
          }}
          animate={{ 
            y: '-200px',
            opacity: [0, sparkle.opacity, 0],
            scale: [0, 1, 0],
          }}
          transition={{
            duration: sparkle.duration,
            repeat: Infinity,
            delay: sparkle.delay,
            ease: "linear"
          }}
          style={{
            width: `${sparkle.size}px`,
            height: `${sparkle.size}px`,
            background: `rgba(147, 51, 234, ${sparkle.opacity})`,
            willChange: 'transform',
            transform: 'translate3d(0,0,0)', // Force GPU layer
          }}
        />
      ))}
    </div>
  );

  return (
    <section className="min-h-screen flex flex-col justify-center items-center text-center px-4 sm:px-6 py-20 relative overflow-hidden">
      {/* Optimized gradient background with GPU acceleration */}
      <div 
        className="absolute inset-0 bg-gradient-to-br from-gray-950 via-slate-900 to-gray-950"
        style={{ transform: 'translate3d(0,0,0)' }}
      />
      
      {/* Simplified gradient mesh with better performance */}
      <div 
        className="absolute inset-0 opacity-30"
        style={{
          background: 'radial-gradient(circle at 20% 30%, rgba(59,130,246,0.3) 0%, transparent 50%), radial-gradient(circle at 80% 70%, rgba(147,51,234,0.2) 0%, transparent 50%)',
          transform: 'translate3d(0,0,0)',
          willChange: 'auto'
        }}
      />
      
      {/* Optimized sparkles */}
      <Sparkles />
      
      <motion.div 
        className="max-w-6xl mx-auto relative z-10"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, ease: "easeOut", type: "tween" }}
        style={{ willChange: 'transform' }}
      >
        {/* Optimized status badge */}
        <motion.div 
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.2, type: "tween" }}
          className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-gradient-to-r from-indigo-500/15 via-purple-500/10 to-pink-500/15 backdrop-blur-sm border border-indigo-500/30 mb-8 shadow-xl group hover:scale-105 transition-transform duration-300"
          style={{ willChange: 'transform' }}
        >
          <div className="relative">
            <span className="inline-flex h-3 w-3 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500" />
          </div>
          <span className="text-sm font-semibold bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
            Limited Access Beta
          </span>
        </motion.div>
        
        {/* Optimized main title */}
        <motion.h1 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3, ease: "easeOut", type: "tween" }}
          className="text-6xl md:text-8xl font-bold mb-8 leading-tight"
          style={{ willChange: 'transform' }}
        >
          <span className="block text-white">
            The Fully Automated
          </span>
          <span className="block mt-4 bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent gradient-text-animation">
            Marketer
          </span>
        </motion.h1>
        
        {/* Enhanced subtitle */}
        <motion.p 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4, ease: "easeOut", type: "tween" }}
          className="text-xl md:text-3xl text-gray-300 mb-12 max-w-4xl mx-auto leading-relaxed"
          style={{ willChange: 'transform' }}
        >
          Vibe your way to MRR with scaled cold email.{' '}
          <span className="text-white font-semibold">
            AI executes every campaign
          </span>{' '}
          from beginning to end so you can scale without headache.
        </motion.p>
        
        {/* Simple decorative divider */}
        <motion.div 
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.5, type: "tween" }}
          className="flex justify-center mb-16"
          style={{ willChange: 'transform' }}
        >
          <div className="relative">
            <div className="w-32 h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-full shadow-xl" />
          </div>
        </motion.div>
        
        {/* Optimized video container */}
        <motion.div 
          initial={{ opacity: 0, y: 50, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.6, ease: "easeOut", type: "tween" }}
          className="w-full px-0 sm:px-6 md:max-w-4xl mx-auto mb-12 sm:mb-16 relative group"
          style={{ willChange: 'transform' }}
        >
          {/* Optimized glow effect */}
          <div 
            className="absolute -inset-4 bg-gradient-to-r from-blue-500/10 to-purple-500/10 blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
            style={{ transform: 'translate3d(0,0,0)' }}
          />
          
          {/* Video wrapper */}
          <div className="relative overflow-hidden rounded-3xl shadow-2xl ring-1 ring-white/10 backdrop-blur-sm">
            {/* Inner content */}
            <div className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-gray-900/90 to-gray-950/90">
              <AspectRatio ratio={16/9} className="w-full relative">
                <iframe 
                  src="https://player.vimeo.com/video/1069192829?title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479"
                  width="100%" 
                  height="100%" 
                  frameBorder="0" 
                  allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
                  title="Live Lead Dashboard"
                  style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                  className="rounded-3xl"
                />
              </AspectRatio>
            </div>
          </div>
        </motion.div>
        
        {/* Optimized action buttons */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8, ease: "easeOut", type: "tween" }}
          className="flex flex-col sm:flex-row items-center justify-center gap-6"
          style={{ willChange: 'transform' }}
        >
          {location.pathname === '/waitlist' && onJoinWaitlist ? (
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              transition={{ type: "tween", duration: 0.2 }}
              style={{ willChange: 'transform' }}
            >
              <Button 
                size="lg" 
                className="group relative bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-12 py-6 text-lg font-bold rounded-full shadow-xl transition-all duration-300" 
                onClick={onJoinWaitlist}
              >
                <span className="relative z-10 flex items-center">
                  Join Waitlist
                  <ArrowRight size={20} className="ml-3" />
                </span>
              </Button>
            </motion.div>
          ) : (
            <>
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                transition={{ type: "tween", duration: 0.2 }}
                style={{ willChange: 'transform' }}
              >
                <button 
                  onClick={handleSignUpClick}
                  className="group relative optimized-button"
                >
                  <div className="relative inline-flex h-16 items-center justify-center rounded-full bg-gradient-to-r from-blue-600 to-purple-600 px-12 text-lg font-semibold text-white shadow-xl transition-all duration-300">
                    <span className="relative z-10 flex items-center">
                      Sign Up Free
                      <ArrowRight size={20} className="ml-3" />
                    </span>
                  </div>
                </button>
              </motion.div>
              
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                transition={{ type: "tween", duration: 0.2 }}
                style={{ willChange: 'transform' }}
              >
                <button 
                  onClick={handleSignInClick}
                  className="group relative optimized-button"
                >
                  <div className="relative inline-flex h-16 items-center justify-center rounded-full bg-white/5 backdrop-blur-sm border-2 border-white/20 px-12 text-lg font-semibold text-white shadow-xl transition-all duration-300 hover:bg-white/10 hover:border-purple-500/50">
                    <span className="relative z-10">Sign In</span>
                  </div>
                </button>
              </motion.div>
            </>
          )}
        </motion.div>
      </motion.div>

      {/* CSS-only gradient animation styles */}
      <style>{`
        .gradient-text-animation {
          background-size: 200% 200%;
          animation: gradientShift 3s ease-in-out infinite;
        }
        
        @keyframes gradientShift {
          0% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
          100% {
            background-position: 0% 50%;
          }
        }
        
        .optimized-button {
          transform: translate3d(0,0,0);
          will-change: transform;
        }
        
        .optimized-button > div {
          transform: translate3d(0,0,0);
        }
      `}</style>
    </section>
  );
};

export default HeroSectionVariantB;
